import { useMemo, useRef, useState } from 'react';

import 'react-tabs/style/react-tabs.css';

import { Stack, Button } from '@mui/material';

/* tema customizado*/
import tableThemeCustom from '../../form/tableThemeCustom';

import formStyles from '../../form/Form.module.css';

/* TABS PANEL */
import SystemTabs from '../../../ui/SystemTabs';
import CaixaTabPanel from './tabs/caixaTabPanel';
import LancamentosTabPanel from './tabs/lancamentosTabPanel';

import { useLancamentoModule } from './modules/useLancamentoModule';

/* hooks */
import { filterTabsByProfile, getInitialTabKey } from '../../../utils/profileTabs';
import { useTabChangeFetch } from '../../../hooks/useTabChangeFetch';
import { useMRTState } from '../../../hooks/useMRTState';

function Form({
    dados,
    setDados,
    caixaModule,
    handleSubmit,

    btnText,
    caminhoVoltar,

    // parametro / profile
    parametro,
    profile,

    // util vindo do CreateOrEdit
    setFlashMessage,
    navigate,

    // escopo pro módulo de lançamentos
    empresaId,
    empresaIds,
    pessoaId,
    optionsConta,
    optionsTipo,
    optionsForma,
}) {
    const urlParams = new URLSearchParams(window.location.search);

    const params = useMemo(
        () =>
            new URLSearchParams({
                ul: urlParams.get('ul'),
                li: urlParams.get('li'),
                tipo: urlParams.get('tipo'),
                operacao: urlParams.get('operacao'),
                uuid: urlParams.get('uuid'),
                btnText: urlParams.get('btnText'),
            }),
        []
    );

    const submittingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();

        if (submittingRef.current) return;

        submittingRef.current = true;
        setIsSubmitting(true);

        try {
            await handleSubmit(dados);
        } finally {
            await new Promise((resolve) => setTimeout(resolve, 3000));

            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const initialTabKey = useMemo(() => getInitialTabKey(profile, { defaultKey: 'caixa' }), [profile]);
    const [activeTabKey, setActiveTabKey] = useState(initialTabKey);

    //table state
    const mrt = useMRTState({
        storageKey: 'caixa.lancamentos',
        pageSizeDefault: parametro?.paginacao ?? 5,
    });

    /* modo tema */
    const theme = useMemo(() => tableThemeCustom(), []);

    // -------------------------
    // módulo de lançamentos (vive aqui)
    // -------------------------
    const lancamentosModule = useLancamentoModule({
        // contexto do caixa
        dados,
        setDados,
        parametro,
        empresaId,
        empresaIds,
        pessoaId,

        // ui/util
        btnText,
        setFlashMessage,
        navigate,

        // options externas
        optionsTipo,
        optionsConta,
        optionsForma,

        // MRT bridge
        mrt,
        activeTabKey,
    });

    // uis por grid
    const uiTabCaixa = profile?.uiTabs?.caixa;
    const uiTabLancamentos = profile?.uiTabs?.lancamentos;

    const tabsConfig = useMemo(() => {
        return [
            {
                key: 'caixa',
                label: 'Caixa',
                title: uiTabCaixa?.titles?.grid,
                panel: <CaixaTabPanel module={caixaModule} profile={profile} />,
            },
            {
                key: 'lancamentos',
                label: 'Lançamentos',
                title: uiTabLancamentos?.titles?.grid,
                panel: <LancamentosTabPanel module={lancamentosModule} profile={profile} theme={theme} mrt={mrt} />,
            },
        ];
    }, [uiTabCaixa, uiTabLancamentos, caixaModule, lancamentosModule, profile, theme, mrt]);

    const tabsConfigFinal = useMemo(() => filterTabsByProfile(tabsConfig, profile, parametro), [tabsConfig, profile, parametro]);

    const { onTabChange } = useTabChangeFetch({
        initialKey: initialTabKey,
        onChange: async (key) => {
            setActiveTabKey(key);

            const refreshActions = {
                caixa: () => caixaModule.actions.onEnterTab(),
                lancamentos: () => lancamentosModule.actions.onEnterTab(),
            };
            if (refreshActions[key]) await refreshActions[key]();
        },
    });

    return (
        <form onSubmit={submit} aria-busy={isSubmitting}>
            <h1>
                {profile?.labelForm} {dados.contador ? `Nº ${dados.contador}` : 'Novo'}
            </h1>
            <SystemTabs tabs={tabsConfigFinal} onTabChange={onTabChange} initialKey={initialTabKey} />
            <div className={formStyles.stiloPadrao}>
                <div>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="secondary" onClick={() => navigate(caminhoVoltar, params)}>
                            Voltar
                        </Button>
                        <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                            {isSubmitting ? 'Salvando...' : btnText}
                        </Button>
                    </Stack>
                </div>
            </div>
        </form>
    );
}

export default Form;
